'use client'

import { QUERY_REFETCH_INTERVALS, QUERY_STALE_TIMES } from '@/constants/query'
import { PLATFORMS, getWalletProvider } from '@/lib/wallets/registry'
import type { BridgePlatform, MarketBalance, WalletRuntime } from '@/lib/wallets/types'
import { useWallet } from '@/providers/WalletContext'
import { useQueries } from '@tanstack/react-query'

export interface UseMarketBalancesOptions {
	runtime?: Partial<WalletRuntime>
}

export function useMarketBalances(
	platforms: readonly BridgePlatform[] = PLATFORMS,
	options: UseMarketBalancesOptions = {}
) {
	const wallet = useWallet()
	const runtime: WalletRuntime = {
		eoaAddress: options.runtime?.eoaAddress ?? wallet.eoaAddress,
		solanaAddress: options.runtime?.solanaAddress ?? wallet.solanaAddress,
		addressOverrides: options.runtime?.addressOverrides,
		publicClient: options.runtime?.publicClient ?? wallet.publicClient,
		basePublicClient: options.runtime?.basePublicClient ?? wallet.basePublicClient,
		solanaConnection: options.runtime?.solanaConnection ?? wallet.solanaConnection,
		polymarketSafe: options.runtime?.polymarketSafe ?? wallet.polymarketSafe,
		polymarketActiveWallet:
			options.runtime?.polymarketActiveWallet ?? wallet.polymarketActiveWallet,
	}

	const results = useQueries({
		queries: platforms.map(platform => {
			const provider = getWalletProvider(platform)
			const address = provider.resolveAddress(runtime)
			return {
				queryKey: provider.balanceQueryKey(address),
				queryFn: () => provider.readBalance(runtime),
				enabled: !!address,
				staleTime: QUERY_STALE_TIMES.BALANCE,
				refetchInterval: QUERY_REFETCH_INTERVALS.BALANCE,
				refetchIntervalInBackground: true,
				refetchOnWindowFocus: true,
			}
		}),
	})

	const balances = Object.fromEntries(
		platforms.map((platform, index) => [platform, results[index].data ?? null])
	) as Partial<Record<BridgePlatform, MarketBalance | null>>

	const isLoading = results.some(result => result.isLoading)
	const isError = results.some(result => result.isError)

	return { balances, results, isLoading, isError }
}
